import { useEffect, useRef, useState, type MouseEvent } from 'react'
import PencilIcon from '../icons/PencilIcon'
import type { PopularModelCard as PopularModelCardType } from '../data/models'
import { cardAutoplayAllowed, useCardVideoAutoplay } from '../utils/cardVideoAutoplay'
import { buildTileVideoQueue, EMPTY_TILE_QUEUE } from '../utils/tileVideoQueue'
import { useTileVideoQueue } from '../hooks/useTileVideoQueue'
import TileVideoWarm from './TileVideoWarm'
import styles from '../styles/components/_popular-models.module.scss'
import { Coin } from '../icons/Coin.tsx'
import { FlowLinkIcon } from '../icons/FlowLink.tsx'
import DropletGlass from './DropletGlass'
import { preBlurredCover } from '../utils/preBlur'
import { trendsLabel } from '../utils/pluralRu'

// Кэш превью-URL, вернувших 404 (webp-бэкфил превью на проде может быть не
// прогнан). Раз получив ошибку — больше не пробуем этот _preview.webp на
// ре-рендерах/скролле, сразу берём оригинал. Живёт до перезагрузки страницы.
const brokenPreviews = new Set<string>()

// Устройство без ховера (телефон/планшет): ховер-превью видео не запускаем.
const TOUCH_DEVICE = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches

/**
 * PopularModelCard — карточка модели/инструмента на главной (горизонтальный
 * скролл) и /bots (grid-разметка через `compact`).
 *
 * Дизайн: hero-image на cover + glass-footer с progressive backdrop-blur +
 * glass-pill цены. На hover — лёгкое размытие картинки + появление arrow-tile
 * в правом верхнем углу. На touch-устройствах arrow-tile видна всегда.
 *
 * Что приходит из данных, но визуально не отображается (поля остаются
 * в типе для совместимости с админкой и контекстом):
 *   - rating, reviews         — раньше были звёзды + число просмотров
 *   - badge                   — раньше «🪄 Готовый промпт» pill сверху
 *   - modelLogoUrl            — раньше logo pill 51×51 в углу
 *
 * Логика клика и data flow НЕ изменены — onClick гардится disabled,
 * routing через targetAdapter/targetModelId/defaultPrompt в item.
 */
type Props = {
	item: PopularModelCardType
	/** Реальная цена с бэка (overrides item.creditsLabel). */
	realCost?: number | string
	/** Не отображается в новом дизайне, оставлено для совместимости. */
	realRating?: number
	/** Не отображается в новом дизайне, оставлено для совместимости. */
	realUsers?: number
	onClick?: () => void
	/** Мульти-тренд (23.10): клик по кадру версии в ряду под названием — открыть группу сразу с этой версией.
	 *  Без колбэка ряд декоративный (как свотчи мультистиля). */
	onPickTrendMember?: (memberId: string) => void
	href?: string
	/** Не используется в новом дизайне (карточка кликабельна целиком). */
	showCta?: boolean
	/** Не используется в новом дизайне. */
	ctaLabel?: string
	/** Уменьшенный режим для grid-разметки (например /bots). */
	compact?: boolean
}

export function PopularModelCard({
	item,
	realCost,
	onClick,
	onPickTrendMember,
	href,
	compact = false,
}: Props) {
	// realRating/realUsers/showCta/ctaLabel принимаем через интерфейс
	// для drop-in совместимости — старые callsites не ломаются.

	// Карточка-флоу: своей модели нет — в пилюле «от N», где N — цена всего результата
	// по ТЕКУЩИМ нейронкам и настройкам узлов шаблона (сумма узлов; владелец 05.09:
	// «от», потому что в пайплайне цена меняется от нейронки и параметров).
	const isFlow = item.cardKind === 'flow'
	const cost = isFlow
		? (item.flowPriceFrom != null ? `от ${item.flowPriceFrom}` : '')
		: (realCost ?? item.creditsLabel)
	const isDisabled = item.disabled === true

	// Ценник плитки. Акция — визуальный акцент: старая цена остаётся в
	// стеклянной пилюле ЗАЧЁРКНУТОЙ, новая клеится поверх «стикером» в
	// градиенте акции (язык .discountBadge). Без акции — прежняя пилюля.
	// Одна JSX-переменная на оба места (ghost-спейсер обтекания + реальный).
	const oldCost = item.discountPercent != null ? item.creditsOldLabel : undefined
	const priceBadge = oldCost ? (
		<span className={styles.badgePromo} aria-label={`${cost} токенов вместо ${oldCost}`}>
			<span className={styles.badgeOld}>
				<span className={styles.badgeOldNum}>{oldCost}</span>
				<Coin width={13} height={13} />
			</span>
			<span className={styles.badgeSticker}>
				<span className={styles.badgeNum}>{cost}</span>
				<Coin width={13} height={13} color="#fff" />
			</span>
		</span>
	) : (
		<span className={styles.badge} aria-label={`${cost} токенов`}>
			<span className={styles.badgeNum}>{cost}</span>
			<Coin width={13} height={13} />
		</span>
	)

	// Видео-превью: беззвучный луп на ховер карточки (поверх постера, накрывая
	// запечённый ▶). Аудио: play/pause по кнопке (клик не открывает инструмент).
	// Тип берём по наличию video_url/audio_url у карточки.
	const videoRef = useRef<HTMLVideoElement>(null)
	const audioRef = useRef<HTMLAudioElement>(null)
	const [audioPlaying, setAudioPlaying] = useState(false)
	const hasVideo = Boolean(item.videoUrl)
	// Автоплей: менеджер решает, играет ли ЭТА плитка сейчас (видна и попала в
	// лимит одновременных). Ховер остаётся вторым входом — им запускается и та
	// плитка, которой лимита не хватило, и весь сценарий без автоплея
	// (экономия трафика, prefers-reduced-motion).
	const [hoverOn, setHoverOn] = useState(false)
	// Автоплей включаем ТОЛЬКО когда у ролика есть лёгкий сосед: иначе плитка
	// тянула бы оригинал (замер: 7 роликов в кадре = 24 МБ). Пока бэкфилл не
	// прогнан — поведение прежнее, ховер; появился сосед — плитка заиграла сама.
	const [autoplayOk] = useState(() => cardAutoplayAllowed())
	const managed = hasVideo && Boolean(item.videoLoopUrl) && autoplayOk
	const { active: autoPlaying, setHovered } = useCardVideoAutoplay(videoRef, managed)
	// Под автоплеем «играет ли» решает менеджер (ховер он учитывает приоритетом);
	// без него — прежний локальный ховер.
	const videoLive = managed ? autoPlaying : hoverOn
	// Наведение перезапускает ролик, но ховер-лифт карточки даёт дребезг
	// enter/leave (замер: три пары событий за 1.6 с) — перематываем не чаще
	// раза в 600 мс, иначе ролик дёргался бы на месте.
	const lastRewind = useRef(0)
	const hasAudio = Boolean(item.audioUrl)
	// Мультистиль (владелец 2026-08-14): плитка живёт БЕЗ «до/после» — главной
	// картинкой стоит ПЕРВАЯ вариация-стиль, под названием ряд свотчей стилей.
	const styleVariants = item.cardKind === 'multistyle'
		? (item.demoVariants ?? []).filter((v) => !v.is_open && v.after_url)
		: []
	const isMsty = styleVariants.length > 0
	// «Свой вариант» (владелец 2026-08-21): чип-карандаш В ХВОСТЕ ряда свотчей —
	// с витрины видно, что можно вписать своё (обложка задана → обычный свотч).
	const openVariants = isMsty ? (item.demoVariants ?? []).filter((v) => v.is_open) : []
	// Мульти-тренд (22.10): плитка одна на группу — в мета-пилюле «N трендов», под названием свотчи кадров
	// участников (тот же ряд, что у стилей мультистиля).
	const trendMembers = item.cardKind === 'multitrend' ? (item.trendMembers ?? []) : []
	// Мульти-тренд (23.10): ролики версий по очереди — состав в utils/tileVideoQueue, ход и прогрев следующего в
	// hooks/useTileVideoQueue. Обычная карточка — очереди нет, ролик играет как раньше.
	const videoQueue = hasVideo && trendMembers.length > 0
		? buildTileVideoQueue({ id: item.id, src: item.videoUrl!, loopSrc: item.videoLoopUrl, poster: item.videoPosterUrl }, trendMembers)
		: EMPTY_TILE_QUEUE
	const vq = useTileVideoQueue(videoRef, videoQueue)
	// Играем ЛЁГКИЙ ролик, если бэк его сделал: оригинал 720p+ со звуком нужен
	// полноэкранному просмотру, а не плитке 250px (см. cardVideoAutoplay).
	const tileVideoSrc = vq.queued && vq.entry ? vq.entry.loopSrc || vq.entry.src : item.videoLoopUrl || item.videoUrl
	// На смене ролика очереди видно постер этой версии; первому ролику — постер плитки, как раньше.
	const queuePoster = vq.queued && !vq.first ? vq.entry?.poster : undefined
	// Hero-картинка: лёгкое превью, если оно не 404-нулось ранее; иначе оригинал.
	const previewOk = !!item.backgroundPreviewUrl && !brokenPreviews.has(item.backgroundPreviewUrl)
	const heroSrc = isMsty
		? styleVariants[0].after_url
		: previewOk ? item.backgroundPreviewUrl! : item.backgroundImageUrl
	// Показать до старта нечего (ни обложки, ни постера) — плиткой служит САМО
	// видео: src держим всегда, иначе там была бы пустота. Байты при этом
	// экономит preload='metadata' — как было до автоплея.
	const videoIsTile = !heroSrc && !item.videoPosterUrl

	// Пре-размытая копия обложки для «стекла» футера: блюр считается один
	// раз в canvas (см. utils/preBlur), дальше это обычная картинка — GPU не
	// пере-блюрит её при каждом входе карточки в вьюпорт. Пока не готова
	// (или canvas недоступен) — прежний CSS-фильтр, вид тот же.
	const [blurredSrc, setBlurredSrc] = useState<string | null>(null)
	useEffect(() => {
		if (!heroSrc) return
		let alive = true
		void preBlurredCover(heroSrc).then((url) => {
			if (alive) setBlurredSrc(url)
		})
		return () => {
			alive = false
		}
	}, [heroSrc])
	// Тач: mouseenter приходит перед тапом, mouseleave — никогда. Превью бы
	// запускалось от тапа и играло невидимым (слой скрыт @media hover:hover)
	// или съедало постер. Без ховера — не играем вовсе.
	// Наведение = посмотреть ролик С НАЧАЛА (владелец 12.09): перематываем на
	// нулевой кадр, даже если плитка уже играет сама. Уход курсора ничего не
	// останавливает — ролик просто продолжается, и следующее наведение снова
	// начнёт его сначала.
	const handleVideoEnter = () => {
		if (TOUCH_DEVICE) return
		const v = videoRef.current
		const now = performance.now()
		if (v?.currentSrc && now - lastRewind.current > 600) {
			lastRewind.current = now
			v.currentTime = 0
		}
		if (managed) {
			setHovered(true)
			return
		}
		setHoverOn(true)
		if (v) void v.play().catch(() => {})
	}
	const handleVideoLeave = () => {
		if (managed) {
			// Уход курсора ролик НЕ останавливает: плитка остаётся в наборе
			// автоплея (менеджер держит её форой) и просто играет дальше.
			setHovered(false)
			return
		}
		setHoverOn(false)
		const v = videoRef.current; if (v) { v.pause(); v.currentTime = 0 }
	}
	// Без автоплея src появляется в DOM только вместе с ховером — play() зовём
	// после рендера (под автоплеем этим занимается менеджер).
	useEffect(() => {
		if (managed || !hoverOn) return
		void videoRef.current?.play().catch(() => {})
	}, [managed, hoverOn])
	const toggleAudio = (e: MouseEvent) => {
		e.stopPropagation()
		const a = audioRef.current
		if (!a) return
		if (a.paused) { void a.play().catch(() => {}); setAudioPlaying(true) }
		else { a.pause(); setAudioPlaying(false) }
	}

	// «Стекло-капля» (админ, tile_style=droplet): host-класс несёт наружную
	// тень-каплю; сам стеклянный оверлей — <DropletGlass/> в конце inner.
	const droplet = item.tileStyle === 'droplet'
	const cardClassName = [
		styles.card,
		compact ? styles.compact : '',
		isDisabled ? styles.disabled : '',
		droplet ? 'glass-droplet-host' : '',
	]
		.filter(Boolean)
		.join(' ')

	const inner = (
		<>
			{/* Hero image — на всю площадь карточки.
			    <img> вместо background-image: lazy loading + alt для screen reader.
			    Обложка рендерится ВСЕГДА, когда задана — даже если у карточки есть
			    видео: видео ложится поверх и открывается только на ховере, так что
			    обложка остаётся статичной плиткой и возвращается после ухода
			    курсора. На poster это переложить нельзя — он живёт лишь до первого
			    play(), дальше элемент отдаёт кадры видео навсегда. */}
			{item.backgroundImageUrl && (
				<img
					className={styles.image}
					// Превью (webp ≤512px) вместо оригинала. Если превью 404-нулось
					// (бэкфил не прогнан) — помечаем его битым и ОДИН раз падаем на
					// оригинал; на ре-рендерах/скролле повторных запросов к битому
					// _preview.webp больше нет (brokenPreviews) — гасит шторм 404.
					src={heroSrc}
					alt={item.title}
					// Лёгкое превью (11-25КБ) грузим сразу: вся сетка ≈1.5МБ, зато при
					// скролле картинки уже на месте (lazy догонял скролл с задержкой —
					// «плитки пустые ~секунду»). Тяжёлый оригинал-фолбэк — лениво.
					loading={previewOk ? 'eager' : 'lazy'}
					decoding="async"
					draggable={false}
					style={isFlow && item.flowCoverPosition ? { objectPosition: item.flowCoverPosition } : undefined}
					onError={(e) => {
						const img = e.currentTarget
						const prev = item.backgroundPreviewUrl
						const orig = item.backgroundImageUrl
						if (prev && !brokenPreviews.has(prev)) {
							brokenPreviews.add(prev)
							if (orig) img.src = orig
						}
					}}
				/>
			)}

			{/* Видео-превью: играет на ховер беззвучным лупом. Обложки нет —
			    видео и есть плитка, постером служит его первый кадр (готовый
			    файл с бэка, а не «вдруг браузер сам декодирует»). */}
			{hasVideo && (
				<>
				<video
					ref={videoRef}
					className={`${styles.video}${item.backgroundImageUrl ? ` ${styles.videoOverlay}` : ''}${
						videoLive ? ` ${styles.videoLive}` : ''
					}`}
					/* src появляется, только когда плитка играет: пока его нет,
					   браузер не держит ни байта и рисует постер. Снятие src —
					   единственный способ вернуть постер после первого play()
					   (спека) и освободить декодер, когда плитка ушла с экрана. */
					src={videoLive || videoIsTile ? tileVideoSrc : undefined}
					poster={queuePoster || heroSrc || item.videoPosterUrl || undefined}
					muted
					/* Очередь версий: без loop, по концу ролика — следующий (useTileVideoQueue). */
					loop={!vq.queued}
					playsInline
					preload={videoIsTile ? 'metadata' : 'none'}
					onEnded={vq.onEnded}
					onTimeUpdate={vq.onTimeUpdate}
					onPause={vq.onStop}
					onEmptied={vq.onStop}
				/>
				<TileVideoWarm entry={vq.warmEntry} />
				</>
			)}

			{/* ОДНА мета-пилюля в левом верхнем (решение владельца 2026-08-18):
			    факты о карточке идут в ней через «·», а не отдельными плашками.
			    Раньше счётчик и «N стилей» ВЫТЕСНЯЛИ друг друга — у мультистиля
			    не было видно, сколько людей карточкой пользовалось. */}
			{(() => {
				const n = typeof item.usedCount === 'number' ? item.usedCount : 0
				const fresh = n < 20
				const stylesN = isMsty ? styleVariants.length : 0
				const stylesWord = stylesN === 1 ? 'стиль' : stylesN < 5 ? 'стиля' : 'стилей'
				const trendsN = trendMembers.length
				return (
					<span
						className={styles.usage}
						aria-label={[
							fresh ? 'Новая карточка' : `${n} генераций`,
							stylesN ? `${stylesN} ${stylesWord}` : '',
							trendsN ? trendsLabel(trendsN) : '',
						].filter(Boolean).join(', ')}
					>
						{fresh ? (
							<>
								<svg className={styles.usageIcon} width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
									<path d="M12 2l1.9 5.8L20 9.6l-6.1 1.8L12 17l-1.9-5.6L4 9.6l6.1-1.8z" />
								</svg>
								Новое
							</>
						) : (
							<>
								<svg className={styles.usageIcon} width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
									<path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z" />
								</svg>
								<span className={styles.usageNum}>{n.toLocaleString('ru-RU')}</span>
							</>
						)}
						{stylesN > 0 && (
							<>
								<span className={styles.metaSep} aria-hidden="true">·</span>
								{stylesN} {stylesWord}
							</>
						)}
						{trendsN > 0 && (
							<>
								<span className={styles.metaSep} aria-hidden="true">·</span>
								{trendsLabel(trendsN)}
							</>
						)}
					</span>
				)
			})()}

			{/* Аудио (audio-карточки): невидимый <audio> + центральная play/pause
			    кнопка. Клик играет/паузит, не открывая инструмент (stopPropagation). */}
			{hasAudio && (
				<>
					<audio ref={audioRef} src={item.audioUrl} preload="none" onEnded={() => setAudioPlaying(false)} />
					<span
						className={styles.audioBtn}
						role="button"
						tabIndex={0}
						aria-label={audioPlaying ? 'Пауза' : 'Слушать'}
						onClick={toggleAudio}
					>
						{audioPlaying ? (
							<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
						) : (
							<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
						)}
					</span>
				</>
			)}

			{/* Glass overlay — стекло БЕЗ живого backdrop-blur: внутри статично
			    размытая копия hero (glassBlurImg), скрим — в ::after футера.
			    Живой backdrop-filter на десятках карточек сетки пересчитывался
			    каждый кадр скролла (замер: ~50мс/кадр, фризы до 2.2с на
			    /bots/images) — blur статичной копии считается один раз.
			    У видео-карточек копия от постера (hover-видео под стеклом
			    остаётся чётким — как и раньше задумано для .video). */}
			<div className={styles.glassFooter} aria-hidden="true">
				{heroSrc && (
					<img
						className={`${styles.glassBlurImg}${blurredSrc ? ` ${styles.glassPre}` : ''}`}
						src={blurredSrc ?? heroSrc}
						alt=""
						loading="lazy"
						decoding="async"
						draggable={false}
					/>
				)}
			</div>

			{/* Текст. Description рендерится всегда (с   если пустой)
			    + min-height в CSS — это даёт фиксированное место под описание,
			    чтобы title во всех карточках сидел на одной вертикальной высоте
			    независимо от длины desc или его отсутствия. */}
			<div className={styles.content}>
				{/* Мультистиль: ряд свотчей-превью стилей НАД названием (владелец
				    2026-08-16 — как в галерейной плитке, структура не ломается). */}
				{isMsty && (
					<span className={styles.multistyleRow} aria-hidden="true">
						{styleVariants.map((v) => (
							<img
								key={v.key}
								className={styles.multistyleSwatch}
								src={v.after_url}
								alt=""
								loading="lazy"
								decoding="async"
								draggable={false}
							/>
						))}
						{openVariants.map((v) => (
							v.after_url ? (
								<img key={v.key} className={styles.multistyleSwatch} src={v.after_url} alt="" loading="lazy" decoding="async" draggable={false} />
							) : (
								<span key={v.key} className={styles.multistyleSwatchOpen} title={v.label}><PencilIcon size={13} /></span>
							)
						))}
					</span>
				)}
				{/* Мульти-тренд: кадры участников над названием — тем же рядом, что свотчи стилей. */}
				{trendMembers.length > 0 && (onPickTrendMember ? (
					// Клик по кадру (23.10) — группа сразу с этой версией; к плитке событие не всплывает.
					<span className={`${styles.multistyleRow} ${styles.multistyleRowPick}`} role="group" aria-label="Тренды группы">
						{trendMembers.map((m) => (
							<span
								key={m.id}
								role="button"
								tabIndex={0}
								className={styles.multistyleSwatchBtn}
								aria-label={`Открыть «${m.title}»`}
								title={m.title}
								onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPickTrendMember(m.id) }}
								onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onPickTrendMember(m.id) } }}
							>
								{/* Кадр версии, чей ролик сейчас на плитке, — с ободком (очередь роликов). */}
								{m.previewUrl
									? <img className={`${styles.multistyleSwatch}${vq.playingId === m.id ? ` ${styles.multistyleSwatchPlaying}` : ''}`} src={m.previewUrl} alt="" loading="lazy" decoding="async" draggable={false} />
									: <span className={`${styles.multistyleSwatchOpen}${vq.playingId === m.id ? ` ${styles.multistyleSwatchPlaying}` : ''}`}>{m.title.slice(0, 2)}</span>}
							</span>
						))}
					</span>
				) : (
					<span className={styles.multistyleRow} aria-hidden="true">
						{trendMembers.map((m) => (
							m.previewUrl
								? <img key={m.id} className={`${styles.multistyleSwatch}${vq.playingId === m.id ? ` ${styles.multistyleSwatchPlaying}` : ''}`} src={m.previewUrl} alt="" loading="lazy" decoding="async" draggable={false} />
								: <span key={m.id} className={`${styles.multistyleSwatchOpen}${vq.playingId === m.id ? ` ${styles.multistyleSwatchPlaying}` : ''}`} title={m.title}>{m.title.slice(0, 2)}</span>
						))}
					</span>
				))}
				{/* Карточка-флоу: единый «флоу-кастом» на всех поверхностях — тонкая линия в
				    цветах портов холста над названием, под ним строка с иконкой и числом шагов,
				    в пилюле — цена всего результата (владелец 05.09). */}
				{isFlow && <span className={styles.flowLine} aria-hidden="true" />}
				<h3 className={styles.title}>{item.title}</h3>
				{isFlow && (
					<span className={styles.flowMeta}>
						<FlowLinkIcon size={15} className={styles.flowMetaIcon} />
						Мастерская{typeof item.flowSteps === 'number' ? ` · ${item.flowSteps} ${item.flowSteps === 1 ? 'шаг' : item.flowSteps < 5 ? 'шага' : 'шагов'}` : ''}
					</span>
				)}
				{/* Описание раскрывается на ховере вместе с подложкой (12.09, владелец: «в покое только заголовок»):
				    внешний блок анимирует высоту строки грида, внутренний обрезает — сам текст своей высоты не меняет,
				    поэтому механика одинаково работает и у флоу-карточек с лишней строкой, и в compact. */}
				<div className={styles.descWrap}>
				<div className={styles.descClip}>
				<p className={styles.desc}>
					{/* descShape — невидимый floated spacer; внутри ghost-копия badge
					    (visibility: hidden) — она задаёт descShape’у точно такую же
					    ширину как у реального badge, без жёсткого захардкода 56px.
					    Текст огибает зону badge (нижний правый угол), сверху использует всю ширину. */}
					{cost !== undefined && cost !== null && cost !== '' && (
						<span className={styles.descShape} aria-hidden="true">
							{priceBadge}
						</span>
					)}
					{item.description || ' '}
				</p>
				</div>
				</div>
			</div>

			{/* Glass price-pill — справа снизу (при акции — зачёркнутая + стикер). */}
			{cost !== undefined && cost !== null && cost !== '' && priceBadge}

			{/* Акция: «−N% · сколько осталось» над прайс-пилюлей. Цена в пилюле
			    уже скидочная (эффективный priceOverride считается на входе). */}
			{item.discountPercent != null && (
				<span
					className={styles.discountBadge}
					aria-label={`Скидка ${item.discountPercent}%`}
				>
					−{item.discountPercent}%{item.discountLeftLabel ? ` · ${item.discountLeftLabel}` : ''}
				</span>
			)}

			{droplet && <DropletGlass />}
		</>
	)

	if (href) {
		return (
			<a
				className={cardClassName}
				href={href}
				data-card-slug={item.slug || undefined}
				aria-label={item.title}
				aria-disabled={isDisabled}
				onClick={(e) => {
					e.preventDefault()
					if (!isDisabled) onClick?.()
				}}
				onMouseEnter={hasVideo ? handleVideoEnter : undefined}
				onMouseLeave={hasVideo ? handleVideoLeave : undefined}
			>
				{inner}
			</a>
		)
	}

	return (
		<button
			type="button"
			className={cardClassName}
			data-card-slug={item.slug || undefined}
			onClick={isDisabled ? undefined : onClick}
			disabled={isDisabled}
			aria-label={item.title}
			onMouseEnter={hasVideo ? handleVideoEnter : undefined}
			onMouseLeave={hasVideo ? handleVideoLeave : undefined}
		>
			{inner}
		</button>
	)
}
